import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToolLaunchDialog } from '@/components/tools/ToolLaunchDialog';
import { toSafeUiMessage } from '@/lib/dashboard-errors';
import { bootstrapPrebuiltTools, resolveToolRuntime } from '@/lib/prebuilt-tools';
import { supabase } from '@/lib/supabase';
import type { Flow, FlowInput } from '@shared/types/flows';
import {
  PREBUILT_TOOL_KEYS,
  type PrebuiltToolKey,
  type ToolRuntimeMissingRequirement,
  type ToolRuntimePublicState,
} from '@shared/types/prebuilt-tools';
import {
  getPrebuiltToolManifest,
  type ToolFieldDefinition,
} from '@shared/types/tool-manifest';
import type { LightViewAction } from '@shared/types/views';

type ToolHandoffAction = Extract<LightViewAction, { type: 'tool_handoff' }>;

type InlineToolConfig =
  | {
      kind: 'prebuilt';
      toolKey: PrebuiltToolKey;
      toolTitle: string;
      composerTitle: string;
      submitLabel: string;
      fields: ToolFieldDefinition[];
      defaultValues: Record<string, string>;
    }
  | {
      kind: 'flow';
      flowId: string;
      toolTitle: string;
      composerTitle: string;
      submitLabel: string;
      fields: ToolFieldDefinition[];
      defaultValues: Record<string, string>;
    };

export interface ActivityInlineToolLaunchResult {
  toolKey: string;
  flowId: string | null;
  executionId: string | null;
  status: ToolRuntimePublicState['status'];
}

interface ActivityInlineToolLauncherProps {
  action: ToolHandoffAction | null;
  onClose: () => void;
  onLaunched: (result: ActivityInlineToolLaunchResult) => void;
}

const isPrebuiltToolKey = (value: string): value is PrebuiltToolKey =>
  PREBUILT_TOOL_KEYS.some((toolKey) => toolKey === value);

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * A tool_handoff is launchable inline only when its toolKey resolves to a
 * prebuilt tool or a user-created flow id. Callers should route unresolvable
 * actions to another surface (e.g. Forge proposal) instead of opening a
 * dead-end launcher.
 */
export const isResolvableToolHandoff = (action: ToolHandoffAction): boolean =>
  isPrebuiltToolKey(action.toolKey) || UUID_PATTERN.test(action.toolKey);

const toToolFieldType = (inputType: FlowInput['type']): ToolFieldDefinition['type'] => {
  if (inputType === 'textarea' || inputType === 'select' || inputType === 'email') {
    return inputType;
  }

  return 'text';
};

const buildFlowFields = (flow: Flow): ToolFieldDefinition[] =>
  flow.inputs.map((input) => ({
    key: input.key,
    label: input.label,
    type: toToolFieldType(input.type),
    required: input.required,
    placeholder: input.defaultValue,
    ...(input.description ? { description: input.description } : {}),
    ...(input.type === 'textarea' ? { rows: 6 } : {}),
    ...(input.options ? { options: input.options } : {}),
  }));

const buildFlowDefaultValues = (flow: Flow): Record<string, string> =>
  flow.inputs.reduce<Record<string, string>>((accumulator, input) => {
    accumulator[input.key] = input.defaultValue ?? input.options?.[0]?.value ?? '';
    return accumulator;
  }, {});

const buildInitialValues = (
  fields: ToolFieldDefinition[],
  defaultValues: Record<string, string>,
  prefill: Record<string, string>
): Record<string, string> => {
  const allowedFieldKeys = new Set(fields.map((field) => field.key));
  const values = { ...defaultValues };

  Object.entries(prefill).forEach(([key, value]) => {
    if (allowedFieldKeys.has(key)) {
      values[key] = value;
    }
  });

  return values;
};

export const ActivityInlineToolLauncher = ({
  action,
  onClose,
  onLaunched,
}: ActivityInlineToolLauncherProps) => {
  const navigate = useNavigate();
  const [userFlow, setUserFlow] = useState<Flow | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [runtimeState, setRuntimeState] = useState<ToolRuntimePublicState | null>(null);
  const [runtimeLoading, setRuntimeLoading] = useState(false);
  const [runtimeSubmitting, setRuntimeSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshIndex, setRefreshIndex] = useState(0);

  const manifest = useMemo(() => {
    if (!action || !isPrebuiltToolKey(action.toolKey)) return null;
    return getPrebuiltToolManifest(action.toolKey);
  }, [action]);
  const flowId = useMemo(() => {
    if (!action || isPrebuiltToolKey(action.toolKey)) return null;
    return UUID_PATTERN.test(action.toolKey) ? action.toolKey : null;
  }, [action]);
  const inlineToolConfig = useMemo<InlineToolConfig | null>(() => {
    if (manifest) {
      return {
        kind: 'prebuilt',
        toolKey: manifest.key,
        toolTitle: manifest.product.title,
        composerTitle: manifest.launch.composerTitle,
        submitLabel: manifest.launch.submitLabel,
        fields: manifest.launch.fields,
        defaultValues: manifest.launch.defaultValues,
      };
    }

    if (flowId && userFlow) {
      return {
        kind: 'flow',
        flowId,
        toolTitle: userFlow.title,
        composerTitle: action?.label ?? userFlow.title,
        submitLabel: action?.label ?? 'Lancer',
        fields: buildFlowFields(userFlow),
        defaultValues: buildFlowDefaultValues(userFlow),
      };
    }

    return null;
  }, [action?.label, flowId, manifest, userFlow]);

  const missingRequirement: ToolRuntimeMissingRequirement | null =
    runtimeState?.status === 'missing_one_thing' ? runtimeState.requirement : null;
  const hasActiveRun =
    runtimeState?.status === 'running' || runtimeState?.status === 'blocked';

  useEffect(() => {
    let cancelled = false;

    if (!action) {
      setUserFlow(null);
      setValues({});
      setRuntimeState(null);
      setError(null);
      return;
    }

    if (!manifest && !flowId) {
      setUserFlow(null);
      setValues({});
      setRuntimeState(null);
      setError('Action non disponible depuis cette page.');
      return;
    }

    if (manifest) {
      setUserFlow(null);
      setValues(
        buildInitialValues(
          manifest.launch.fields,
          manifest.launch.defaultValues,
          action.prefill
        )
      );
    }
    setRuntimeState(null);
    setError(null);
    setRuntimeLoading(true);

    const loadRuntime = async () => {
      try {
        if (manifest) {
          await bootstrapPrebuiltTools({ toolKeys: [manifest.key] });
        }

        if (flowId) {
          const { data, error: flowError } = await supabase
            .from('flows')
            .select('*')
            .eq('id', flowId)
            .neq('status', 'archived')
            .maybeSingle();

          if (flowError) {
            throw flowError;
          }
          if (!data) {
            throw new Error('Tool introuvable.');
          }

          const flow = data as Flow;
          if (!cancelled) {
            setUserFlow(flow);
            const fields = buildFlowFields(flow);
            setValues(
              buildInitialValues(
                fields,
                buildFlowDefaultValues(flow),
                action.prefill
              )
            );
          }
        }

        const runtime = await resolveToolRuntime({
          ...(manifest ? { toolKey: manifest.key } : { flowId: flowId ?? '' }),
          submission: null,
          clientContext: {
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            locale: navigator.language,
          },
        });
        if (!cancelled) setRuntimeState(runtime);
      } catch (loadError) {
        if (!cancelled) {
          setError(
            toSafeUiMessage(
              loadError instanceof Error ? loadError.message : null,
              'Impossible de préparer cette action.'
            )
          );
        }
      } finally {
        if (!cancelled) setRuntimeLoading(false);
      }
    };

    void loadRuntime();

    return () => {
      cancelled = true;
    };
  }, [action, flowId, manifest, refreshIndex]);

  if (!action) return null;

  if (!inlineToolConfig) {
    return (
      <ToolLaunchDialog
        open
        toolTitle="Tool"
        composerTitle="Préparation"
        submitLabel="Lancer"
        fields={[]}
        values={{}}
        missingRequirement={null}
        hasActiveRun={hasActiveRun}
        runtimeLoading={runtimeLoading}
        runtimeSubmitting={false}
        submitDisabled
        error={error}
        onClose={onClose}
        onSubmit={() => undefined}
        onRefresh={() => {
          setRuntimeState(null);
          setError(null);
          setRefreshIndex((currentIndex) => currentIndex + 1);
        }}
        onValueChange={() => undefined}
      />
    );
  }

  const normalizedValues = Object.entries(values).reduce<Record<string, string>>(
    (accumulator, [key, value]) => {
      accumulator[key] = value.trim();
      return accumulator;
    },
    {}
  );
  const missingRequiredField = inlineToolConfig.fields.some(
    (field) => field.required && !(normalizedValues[field.key] ?? '')
  );
  const submitDisabled =
    runtimeSubmitting ||
    (!missingRequirement?.isConnector && missingRequiredField);

  const submit = async () => {
    if (missingRequirement?.isConnector) {
      navigate('/connections');
      return;
    }

    if (submitDisabled) return;

    setRuntimeSubmitting(true);
    setError(null);

    try {
      const runtimeTarget =
        inlineToolConfig.kind === 'prebuilt'
          ? { toolKey: inlineToolConfig.toolKey }
          : { flowId: inlineToolConfig.flowId };
      const runtime = await resolveToolRuntime({
        ...runtimeTarget,
        submission: inlineToolConfig.kind === 'prebuilt' && inlineToolConfig.toolKey === 'analyze_call'
          ? {
              kind: 'paste_transcript',
              transcript: normalizedValues.transcript ?? '',
            }
          : {
              kind: 'provide_inputs',
              inputValues: normalizedValues,
            },
        clientContext: {
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          locale: navigator.language,
        },
        handoffOrigin: action.origin ?? null,
      });

      setRuntimeState(runtime);
      if (
        runtime.status === 'missing_one_thing' ||
        runtime.status === 'ready_immediately'
      ) {
        return;
      }

      onLaunched({
        toolKey: runtime.toolKey,
        flowId: runtime.flowId,
        executionId:
          runtime.status === 'running' || runtime.status === 'blocked'
            ? runtime.executionId
            : runtime.status === 'workspace_ready'
              ? runtime.executionId
              : null,
        status: runtime.status,
      });
      onClose();
    } catch (submitError) {
      setError(
        toSafeUiMessage(
          submitError instanceof Error ? submitError.message : null,
          'Impossible de lancer cette action.'
        )
      );
    } finally {
      setRuntimeSubmitting(false);
    }
  };

  return (
    <ToolLaunchDialog
      open
      toolTitle={inlineToolConfig.toolTitle}
      composerTitle={inlineToolConfig.composerTitle}
      submitLabel={inlineToolConfig.submitLabel}
      fields={inlineToolConfig.fields}
      values={values}
      missingRequirement={missingRequirement}
      hasActiveRun={hasActiveRun}
      runtimeLoading={runtimeLoading}
      runtimeSubmitting={runtimeSubmitting}
      submitDisabled={submitDisabled}
      error={error}
      onClose={onClose}
      onSubmit={() => void submit()}
      onRefresh={() => {
        setRuntimeState(null);
        setError(null);
        setRefreshIndex((currentIndex) => currentIndex + 1);
      }}
      onValueChange={(key, value) =>
        setValues((currentValues) => ({
          ...currentValues,
          [key]: value,
        }))
      }
    />
  );
};
